// React and Router imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"

// Firebase imports
import { auth } from "../../firebase.js"

// Component imports
import SnapshotModal from "./SnapshotModal";
import OrphanedDocumentsModal from "../shared/OrphanedDocumentsModal";
import ConvertDocumentModal from "../shared/ConvertDocumentModal";
import NewFromTemplateButton from "../shared/templates/NewFromTemplateButton";

// Styles
import "../../css/DocumentationHomeComponent.css";
import "../../css/Create.css";


function DocumentationHomeComponent() {
        const [loading, setLoading] = useState(true);
        const [loadingMessage, setLoadingMessage] = useState('Checking authentication...');
        const [uid, setUid] = useState("")
        const navigate = useNavigate();
        const [hasSpaces, setHasSpaces] = useState(false)
        const [favorites, setFavorites] = useState([]);
        const [hasFavorites, setHasFavorites] = useState(false);
        const [recentlyViewed, setRecentlyViewed] = useState([]);
        const [hasRecentlyViewed, setHasRecentlyViewed] = useState(false);
        const [staleDocuments, setStaleDocuments] = useState([]);
        const [hasStaleDocuments, setHasStaleDocuments] = useState(false);
        const [snapshots, setSnapshots] = useState([]);
        const [hasSnapshots, setHasSnapshots] = useState(false);
        const [snapshotsExpanded, setSnapshotsExpanded] = useState(false);
        const [showSnapshotModal, setShowSnapshotModal] = useState(false);
        const [selectedSnapshot, setSelectedSnapshot] = useState(null);
        const [authResolved, setAuthResolved] = useState(false);
        const [fetchesResolved, setFetchesResolved] = useState(false);
        const [showOrphanedDocsModal, setShowOrphanedDocsModal] = useState(false);
        const [showConvertModal, setShowConvertModal] = useState(false);

        useEffect(() => {
                setLoading(true);
                setLoadingMessage('Checking authentication...');
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (user) {
                                setUid(user.uid)
                        } else {
                                setUid(null);
                                setLoading(false); // Dismiss loading if not authenticated
                        }
                        setAuthResolved(true);
                })
                return () => { unsubscribe() }
        }, [])

        // Theme cleanup on unmount



        useEffect(() => { 
                let didCancel = false;
                if (uid) { 
                        setLoading(true);
                        setLoadingMessage('Fetching documentation...');
                        let completed = 0;
                        const markDone = () => {
                                completed += 1;
                                if (completed === 5 && !didCancel) {
                                        setFetchesResolved(true);
                                }
                        };
                        const fetchPageDetailsWrapper = async () => {
                                await fetchPageDetails();
                                markDone();
                        };
                        const fetchFavoritesWrapper = async () => {
                                await fetchFavorites();
                                markDone();
                        };
                        const fetchRecentlyViewedWrapper = async () => {
                                await fetchRecentlyViewed();
                                markDone();
                        };
                        const fetchStaleDocumentsWrapper = async () => {
                                await fetchStaleDocuments();
                                markDone();
                        };
                        const fetchSnapshotsWrapper = async () => {
                                await fetchSnapshots();
                                markDone();
                        };
                        setFetchesResolved(false);
                        fetchPageDetailsWrapper();
                        fetchFavoritesWrapper();
                        fetchRecentlyViewedWrapper();
                        fetchStaleDocumentsWrapper();
                        fetchSnapshotsWrapper();
                }
                return () => { didCancel = true; };
        }, [uid]);

        const fetchPageDetails = async () => {
                try {
                        if (uid.length !== 0) {
                                const token = await auth.currentUser.getIdToken();
                                setLoadingMessage('Fetching documentation...');
                                const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/" + uid + "/", {
                                        method: "GET",
                                        headers: { "Content-Type": "application/json", "Authorization": `${token}` }
                                });
                    
                                if (response.status === 200) {
                                        setHasSpaces(true)
                                        setLoading(false)
                                } else if (response.status === 404) {
                                        setHasSpaces(false)
                                        setLoading(false)
                                } else {
                                        console.log("Failed to fetch page detail data.");
                                }
                        }

                } catch (error) {
                        console.error("Error getting data from page detail table:", error);
                }
        }

        const fetchFavorites = async () => {
                try {
                        if (uid.length !== 0) {
                                setLoadingMessage('Fetching favorites...');
                                const user = auth.currentUser;
                                if (!user) return;
                                
                                const token = await user.getIdToken();
                                
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/favorites/get-favorites/${user.uid}/`, {
                                        method: "GET",
                                        headers: {
                                                "Content-Type": "application/json",
                                                "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                        }
                                });
                                
                                if (response.ok) {
                                        const favoritesData = await response.json();
                                        setFavorites(favoritesData);
                                        setHasFavorites(favoritesData.length > 0);
                                        setLoading(false);
                                } else {
                                        setLoading(false);
                                }
                        }
                } catch (error) {
                        console.error("Error getting favorites data:", error);
                        setLoading(false);
                }
        }

        const fetchRecentlyViewed = async () => {
                try {
                        if (uid.length !== 0) {
                                setLoadingMessage('Fetching recently viewed...');
                                const user = auth.currentUser;
                                if (!user) return;
                                
                                const token = await user.getIdToken();
                                
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/${uid}/`, {
                                        method: "GET",
                                        headers: {
                                                "Content-Type": "application/json",
                                                "Authorization": `${token}`,
                                        }
                                });
                                
                                if (response.ok) {
                                        const allPages = await response.json();
                                        
                                        // Filter pages with last_viewed_at and sort by most recent
                                        const viewedPages = allPages
                                                .filter(page => page.last_viewed_at !== null)
                                                .sort((a, b) => new Date(b.last_viewed_at) - new Date(a.last_viewed_at))
                                                .slice(0, 10); // Get top 10 most recent
                                        
                                        // Transform to match favorites structure
                                        const recentlyViewedData = viewedPages.map(page => ({
                                                document_name: page.space || page.page_name,
                                                document_type: page.space ? 'Space' : 'Page',
                                                author_uid: page.author,
                                                last_viewed_at: page.last_viewed_at
                                        }));
                                        
                                        setRecentlyViewed(recentlyViewedData);
                                        setHasRecentlyViewed(recentlyViewedData.length > 0);
                                        setLoading(false);
                                } else {
                                        setLoading(false);
                                }
                        }
                } catch (error) {
                        console.error("Error getting recently viewed data:", error);
                        setLoading(false);
                }
        }

        const fetchStaleDocuments = async () => {
                try {
                        if (uid.length !== 0) {
                                setLoadingMessage('Checking for stale documents...');
                                const user = auth.currentUser;
                                if (!user) return;
                                
                                const token = await user.getIdToken();
                                
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/${uid}/`, {
                                        method: "GET",
                                        headers: {
                                                "Content-Type": "application/json",
                                                "Auth-Token": `Bearer ${token}`,
                                                "Authorization": `${token}`,
                                                "Requesting-User-Uid": user.uid
                                        }
                                });
                                
                                if (response.ok) {
                                        const data = await response.json();
                                        
                                        // Calculate stale threshold (90 days ago)
                                        const ninetyDaysAgo = new Date();
                                        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                                        
                                        // Filter for documents whose most recent activity is 90+ days old.
                                        // A stale-dismissal counts as activity, so dismissed docs drop off the
                                        // widget until they go stale again (see dismiss handler below).
                                        const staleDocsList = data.filter(doc => {
                                                const activityDates = [doc.last_viewed_at, doc.updated_at, doc.stale_dismissed_at]
                                                        .filter(Boolean)
                                                        .map(dateStr => new Date(dateStr));

                                                const mostRecentActivity = activityDates.length
                                                        ? new Date(Math.max(...activityDates))
                                                        : null;

                                                // Include if no activity dates OR if most recent activity is older than 90 days
                                                return !mostRecentActivity || mostRecentActivity < ninetyDaysAgo;
                                        });
                                        
                                        // Sort by last activity (oldest first)
                                        staleDocsList.sort((a, b) => {
                                                const aDate = a.last_viewed_at || a.updated_at;
                                                const bDate = b.last_viewed_at || b.updated_at;
                                                
                                                if (!aDate && !bDate) return 0;
                                                if (!aDate) return -1;
                                                if (!bDate) return 1;
                                                
                                                return new Date(aDate) - new Date(bDate);
                                        });
                                        
                                        // Limit to top 10
                                        const topStaleDocuments = staleDocsList.slice(0, 10).map(doc => ({
                                                document_name: doc.space || doc.page_name,
                                                document_type: doc.space ? 'space' : 'page',
                                                author_uid: doc.author,
                                                last_viewed_at: doc.last_viewed_at,
                                                updated_at: doc.updated_at
                                        }));
                                        
                                        setStaleDocuments(topStaleDocuments);
                                        setHasStaleDocuments(topStaleDocuments.length > 0);
                                } else {
                                        console.log("Failed to fetch stale documents data.");
                                }
                        }
                } catch (error) {
                        console.error("Error getting stale documents data:", error);
                }
        }

        const fetchSnapshots = async () => {
                try {
                        if (uid.length !== 0) {
                                setLoadingMessage('Fetching snapshots...');
                                const user = auth.currentUser;
                                if (!user) return;
                                
                                const token = await user.getIdToken();
                                
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/snapshots/personal/list/`, {
                                        method: "GET",
                                        headers: {
                                                "Content-Type": "application/json",
                                                "Auth-Token": `Bearer ${token}`,
                                                "Authorization": `${token}`,
                                                "Requesting-User-Uid": user.uid
                                        }
                                });
                                
                                if (response.ok) {
                                        const snapshotsData = await response.json();
                                        setSnapshots(snapshotsData.snapshots || []);
                                        setHasSnapshots((snapshotsData.snapshots || []).length > 0);
                                } else {
                                        console.log("Failed to fetch snapshots data.", response.status, response.statusText);
                                        const errorText = await response.text();
                                        console.error("Error response:", errorText);
                                }
                        }
                } catch (error) {
                        console.error("Error getting snapshots data:", error);
                }
        }

        const handleCreateClick = () => { navigate("/create") }

        const handleDocumentClick = (doc) => {
                // Use the author_uid from the favorite if available, otherwise fall back to current user's uid
                const documentAuthorUid = doc.author_uid || uid;
                navigate(`/documentation/${doc.document_type.toLowerCase()}/${doc.document_name}/${documentAuthorUid}`);
        }

        const formatDate = (dateString) => {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                });
        }

        const renderFavorites = () => {
                if (!favorites || favorites.length === 0) {
                        return <p>No favorites yet. Star documents to add them here.</p>;
                }
                
                return (
                        <ul className="DocumentationHomeComponent__document-list">
                                {favorites.map((doc, index) => (
                                        <li 
                                                key={index} 
                                                className="DocumentationHomeComponent__document-item"
                                                onClick={() => handleDocumentClick(doc)}
                                                style={{ cursor: 'pointer' }}
                                        >
                                                <div className="DocumentationHomeComponent__document-info">
                                                        <span className="DocumentationHomeComponent__document-name">
                                                                {doc.document_name}
                                                                <span className="DocumentationHomeComponent__document-type">
                                                                        {doc.author_uid != uid ? '(Shared)' : ''}
                                                                </span>
                                                        </span>
                                                        <span className="DocumentationHomeComponent__document-date">
                                                                {doc.document_type}
                                                        </span>
                                                </div>
                                        </li>
                                ))}
                        </ul>
                );
        }

        const renderRecentlyViewed = () => {
                if (!recentlyViewed || recentlyViewed.length === 0) {
                        return <p>No recently viewed documents yet.</p>;
                }
                
                return (
                        <ul className="DocumentationHomeComponent__document-list">
                                {recentlyViewed.map((doc, index) => (
                                        <li 
                                                key={index} 
                                                className="DocumentationHomeComponent__document-item"
                                                onClick={() => handleDocumentClick(doc)}
                                                style={{ cursor: 'pointer' }}
                                        >
                                                <div className="DocumentationHomeComponent__document-info">
                                                        <span className="DocumentationHomeComponent__document-name">
                                                                {doc.document_name}
                                                                <span className="DocumentationHomeComponent__document-type">
                                                                        {doc.author_uid != uid ? '(Shared)' : ''}
                                                                </span>
                                                        </span>
                                                        <span className="DocumentationHomeComponent__document-date">
                                                                {doc.document_type}
                                                        </span>
                                                </div>
                                        </li>
                                ))}
                        </ul>
                );
        }

        const handleDismissStaleDocument = async (doc, event) => {
                // Prevent the row's navigation click from firing
                event.stopPropagation();

                // Optimistically remove the entry from the widget
                setStaleDocuments(prev => {
                        const updated = prev.filter(d => !(d.document_name === doc.document_name && d.document_type === doc.document_type));
                        setHasStaleDocuments(updated.length > 0);
                        return updated;
                });

                try {
                        const user = auth.currentUser;
                        if (!user) return;

                        const token = await user.getIdToken();

                        await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/dismiss-stale/${doc.document_type}/${encodeURIComponent(doc.document_name)}/${doc.author_uid}/`, {
                                method: "PUT",
                                headers: {
                                        "Content-Type": "application/json",
                                        "Auth-Token": `Bearer ${token}`,
                                        "Authorization": `${token}`,
                                        "Requesting-User-Uid": user.uid
                                }
                        });
                } catch (error) {
                        console.error("Error dismissing stale document:", error);
                }
        }

        const renderStaleDocuments = () => {
                if (!staleDocuments || staleDocuments.length === 0) {
                        return <p>No stale documents found. Great job keeping your documents active!</p>;
                }

                const formatLastActivity = (doc) => {
                        const lastViewed = doc.last_viewed_at ? new Date(doc.last_viewed_at) : null;
                        const lastUpdated = doc.updated_at ? new Date(doc.updated_at) : null;
                        
                        const mostRecentActivity = lastViewed && lastUpdated 
                                ? new Date(Math.max(lastViewed, lastUpdated))
                                : lastViewed || lastUpdated;
                        
                        if (!mostRecentActivity) {
                                return 'Never accessed';
                        }
                        
                        const daysSince = Math.floor((new Date() - mostRecentActivity) / (1000 * 60 * 60 * 24));
                        return `${daysSince} days ago`;
                };
                
                return (
                        <ul className="DocumentationHomeComponent__document-list">
                                {staleDocuments.map((doc, index) => (
                                        <li 
                                                key={index} 
                                                className="DocumentationHomeComponent__document-item DocumentationHomeComponent__stale-document-item"
                                                onClick={() => handleDocumentClick(doc)}
                                                style={{ cursor: 'pointer' }}
                                        >
                                                <div className="DocumentationHomeComponent__document-info">
                                                        <span className="DocumentationHomeComponent__document-name">
                                                                {doc.document_name}
                                                        </span>
                                                        <span className="DocumentationHomeComponent__stale-indicator">
                                                                <i className="fa-solid fa-clock"></i> {formatLastActivity(doc)}
                                                        </span>
                                                </div>
                                                <button
                                                        type="button"
                                                        className="DocumentationHomeComponent__stale-dismiss-btn"
                                                        onClick={(e) => handleDismissStaleDocument(doc, e)}
                                                        title="Dismiss for now — reappears if it goes stale again"
                                                        aria-label={`Dismiss ${doc.document_name} from stale documents`}
                                                >
                                                        <i className="fa-solid fa-xmark"></i>
                                                </button>
                                        </li>
                                ))}
                        </ul>
                );
        }

        const handleSnapshotClick = (snapshot) => {
                // Open modal to view snapshot details
                setSelectedSnapshot(snapshot);
                setShowSnapshotModal(true);
        }

        const formatSnapshotDate = (dateString) => {
                const date = new Date(dateString);
                const now = new Date();
                const diffInMinutes = Math.floor((now - date) / (1000 * 60));
                
                if (diffInMinutes < 1) return 'Just now';
                if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
                if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
                if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
                
                return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                });
        }

        const getSnapshotPreview = (content) => {
                if (!content) return 'No content';
                // Strip HTML tags and get first 100 characters
                const textContent = content.replace(/<[^>]*>/g, '').trim();
                return textContent.length > 100 ? textContent.substring(0, 100) + '...' : textContent;
        }

        const renderSnapshots = () => {
                if (!snapshots || snapshots.length === 0) {
                        return <p className="DocumentationHomeComponent__no-items">No recent snapshots found.</p>;
                }
                
                const recentSnapshot = snapshots[0]; // Most recent snapshot
                const totalSnapshots = snapshots.length;
                
                return (
                        <div className="DocumentationHomeComponent__snapshots-container">
                                <div 
                                        className="DocumentationHomeComponent__snapshots-summary"
                                        onClick={() => setSnapshotsExpanded(!snapshotsExpanded)}
                                >
                                        <div className="DocumentationHomeComponent__snapshots-summary-content">
                                                <div className="DocumentationHomeComponent__snapshots-recent">
                                                        <span className="DocumentationHomeComponent__snapshots-count">
                                                                {totalSnapshots} recent snapshot{totalSnapshots !== 1 ? 's' : ''}
                                                        </span>
                                                        <span className="DocumentationHomeComponent__snapshots-latest">
                                                                Latest: {recentSnapshot.document_name || 'Untitled Document'} • {formatSnapshotDate(recentSnapshot.created_at)}
                                                        </span>
                                                </div>
                                                <div className="DocumentationHomeComponent__snapshots-toggle">
                                                        <span className={`DocumentationHomeComponent__dropdown-arrow ${snapshotsExpanded ? 'expanded' : ''}`}>
                                                                ▼
                                                        </span>
                                                </div>
                                        </div>
                                </div>
                                
                                {snapshotsExpanded && (
                                        <div className="DocumentationHomeComponent__snapshots-grid">
                                                {snapshots.map((snapshot, index) => (
                                                        <div 
                                                                key={index} 
                                                                className="DocumentationHomeComponent__snapshot-card"
                                                                onClick={() => handleSnapshotClick(snapshot)}
                                                        >
                                                                <div className="DocumentationHomeComponent__snapshot-header">
                                                                        <div className="DocumentationHomeComponent__snapshot-title">
                                                                                <h4>{snapshot.document_name || 'Untitled Document'}</h4>
                                                                                {snapshot.document_parent_page && (
                                                                                        <span className="DocumentationHomeComponent__snapshot-parent">
                                                                                                📄 {snapshot.document_parent_page}
                                                                                        </span>
                                                                                )}
                                                                                <span className={`DocumentationHomeComponent__snapshot-type DocumentationHomeComponent__snapshot-type--${snapshot.document_type}`}>
                                                                                        {snapshot.document_type}
                                                                                </span>
                                                                        </div>
                                                                        <span className="DocumentationHomeComponent__snapshot-time">
                                                                                {formatSnapshotDate(snapshot.created_at)}
                                                                        </span>
                                                                </div>
                                                                <div className="DocumentationHomeComponent__snapshot-preview">
                                                                        {getSnapshotPreview(snapshot.content)}
                                                                </div>
                                                                <div className="DocumentationHomeComponent__snapshot-footer">
                                                                        <span className="DocumentationHomeComponent__snapshot-status">
                                                                                {snapshot.completed ? '✓ Complete' : '○ In Progress'}
                                                                        </span>
                                                                        {snapshot.is_public && (
                                                                                <span className="DocumentationHomeComponent__snapshot-public">🌐 Public</span>
                                                                        )}
                                                                </div>
                                                        </div>
                                                ))}
                                        </div>
                                )}
                        </div>
                );
        }

        const handleHomeButtonClick = () => { navigate("/") }
        const handleLoginButtonClick = () => { navigate("/login") }
        const handleRegisterButtonClick = () => { navigate("/register") }
            
        if (loading) {
                return (
                        <div className="DocumentationHomeComponent__coffee-loader-div">
                                <div className="coffee-cup-loader">
                                        <div className="cup">
                                                <div className="cup-body"></div>
                                                <div className="cup-handle"></div>
                                                <div className="cup-base"></div>
                                                <div className="cup-shadow"></div>
                                                <div className="steam steam1"></div>
                                                <div className="steam steam2"></div>
                                                <div className="steam steam3"></div>
                                        </div>
                                </div>
                                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                        </div>
                )
        }

        if (!uid && authResolved) {
                return (
                        <div className="DocumentationMainPage__div">
                                <div className="DocumentationMainPage__main-content">
                                        <p className="large">It looks like you're not signed in.</p>
                                        <p className="medium">
                                                <strong>To create a document, you must have an account with us</strong>. If you already have an account, you can login by clicking the Login button 
                                                below. If you don't already have an account, you can create a new one by clicking the Register button below.
                                        </p>
                                        <button onClick={handleHomeButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                                        <button onClick={handleLoginButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                                        <button onClick={handleRegisterButtonClick} className="ConflictResolver__button">Register</button>
                                </div>
                        </div>
                )
        }

        if (loading === false && fetchesResolved && hasSpaces === false) {
                return (
                        <div>
                                <p className="large">
                                        Looks like you haven't created any documentation yet! <strong>Click the button below to get started</strong>.
                                </p>
                                <button onClick={handleCreateClick} className="submit-button">Create</button>
                                {hasFavorites && (
                                        <div className="DocumentationHomeComponent__favorites-div">
                                                <h3>Your Favorites</h3>
                                                {renderFavorites()}
                                        </div>
                                )}
                                {hasRecentlyViewed && (
                                        <div className="DocumentationHomeComponent__recently-viewed-div">
                                                <h3>Recently Viewed</h3>
                                                {renderRecentlyViewed()}
                                        </div>
                                )}
                                {hasStaleDocuments && (
                                        <div className="DocumentationHomeComponent__stale-documents-div">
                                                <h3>
                                                        Stale Documents <i className="fa-solid fa-info-circle" title="Documents not viewed or edited in 90+ days"></i>
                                                </h3>
                                                <p className="DocumentationHomeComponent__stale-subtitle">
                                                        These documents haven't been viewed or edited in over 90 days. Consider reviewing them for deletion.
                                                </p>
                                                {renderStaleDocuments()}
                                        </div>
                                )}
                                {hasSnapshots && (
                                        <div className="DocumentationHomeComponent__snapshots-section">
                                                <h3>Recent Snapshots</h3>
                                                <p className="DocumentationHomeComponent__snapshots-subtitle">
                                                        Auto-saved drafts from your editing sessions
                                                </p>
                                                {renderSnapshots()}
                                        </div>
                                )}
                                <SnapshotModal
                                        isOpen={showSnapshotModal}
                                        onClose={() => setShowSnapshotModal(false)}
                                        snapshot={selectedSnapshot}
                                />
                        </div>
                )
        }

        if (loading === false && fetchesResolved && hasSpaces === false) {/* Lines 608-653 omitted */}

        return(
                <div>
                        <div className="DocumentationHomeComponent__dashboard-header">
                                <h1 className="DocumentationHomeComponent__dashboard-h1">Dashboard</h1>
                                <div className="DocumentationHomeComponent__quick-actions">
                                        <NewFromTemplateButton
                                                createPath="/create"
                                                className="DocumentationHomeComponent__analyze-duplicates-btn"
                                        />
                                        <button
                                                className="DocumentationHomeComponent__analyze-duplicates-btn"
                                                onClick={() => navigate('/documentation/advanced-search')}
                                                title="Search your documentation by title, description, content, and date"
                                        >
                                                <i className="fas fa-search"></i>
                                                <span>Search</span>
                                        </button>
                                        <button
                                                className="DocumentationHomeComponent__orphaned-docs-btn"
                                                onClick={() => setShowOrphanedDocsModal(true)}
                                                title="Check for orphaned documents"
                                        >
                                                <i className="fa-solid fa-triangle-exclamation"></i>
                                                <span>Data Integrity</span>
                                        </button>
                                        <button
                                                className="DocumentationHomeComponent__analyze-duplicates-btn"
                                                onClick={() => navigate('/documentation/analyze-duplicates')}
                                                title="Find duplicate content across your documents"
                                        >
                                                <i className="fa-solid fa-search"></i>
                                                <span>Duplicates</span>
                                        </button>
                                        <button
                                                className="DocumentationHomeComponent__analyze-duplicates-btn"
                                                onClick={() => setShowConvertModal(true)}
                                                title="Convert documents between personal and workspace"
                                        >
                                                <i className="fas fa-exchange-alt"></i>
                                                <span>Convert</span>
                                        </button>
                                </div>
                        </div>

                        {hasFavorites && (
                                <div className="DocumentationHomeComponent__favorites-div">
                                        <h3>Your Favorites</h3>
                                        {renderFavorites()}
                                </div>
                        )}

                        {hasRecentlyViewed && (
                                <div className="DocumentationHomeComponent__recently-viewed-div">
                                        <h3>Recently Viewed</h3>
                                        {renderRecentlyViewed()}
                                </div>
                        )}

                        {hasStaleDocuments && (
                                <div className="DocumentationHomeComponent__stale-documents-div">
                                        <h3>
                                                Stale Documents <i className="fa-solid fa-info-circle" title="Documents not viewed or edited in 90+ days"></i>
                                        </h3>
                                        <p className="DocumentationHomeComponent__stale-subtitle">
                                                These documents haven't been viewed or edited in over 90 days. Consider reviewing them for deletion.
                                        </p>
                                        {renderStaleDocuments()}
                                </div>
                        )}

                        {hasSnapshots && (
                                <div className="DocumentationHomeComponent__snapshots-section">
                                        <h3>Recent Snapshots</h3>
                                        <p className="DocumentationHomeComponent__snapshots-subtitle">
                                                Auto-saved drafts from your editing sessions
                                        </p>
                                        {renderSnapshots()}
                                </div>
                        )}

                        <SnapshotModal
                                isOpen={showSnapshotModal}
                                onClose={() => setShowSnapshotModal(false)}
                                snapshot={selectedSnapshot}
                        />

                        <OrphanedDocumentsModal
                                isOpen={showOrphanedDocsModal}
                                onClose={() => setShowOrphanedDocsModal(false)}
                                uid={uid}
                        />

                        <ConvertDocumentModal
                                isOpen={showConvertModal}
                                onClose={() => setShowConvertModal(false)}
                                preSelectedDirection="personal_to_workspace"
                        />
                </div>
        )
}

export default DocumentationHomeComponent